import React, { useCallback, useEffect, useRef, useState } from 'react'

import { TooltipModel, Chart } from 'chart.js'

import {
  HIDDEN,
  type TooltipData,
  type TooltipItem,
  type Position
} from '../types'

const SCROLL_DEBOUNCE_MS = 150
const TOOLTIP_FALLBACK_WIDTH = 220
const TOOLTIP_FALLBACK_HEIGHT = 160
const TOOLTIP_GAP_PX = 12
const VIEWPORT_PADDING_PX = 8
const BAND_HIGH_LABEL = '_bandHigh'
const BAND_LOW_LABEL = '_bandLow'

export function useSpendTooltip(
  getItemsRef?: React.MutableRefObject<
    ((index: number) => TooltipItem[]) | undefined
  >
) {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)
  const [position, setPosition] = useState<Position>(HIDDEN)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollingRef = useRef(false)

  useEffect(() => {
    const handleScroll = () => {
      scrollingRef.current = true
      setPosition(HIDDEN)
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
      scrollTimerRef.current = setTimeout(() => {
        scrollingRef.current = false
      }, SCROLL_DEBOUNCE_MS)
    }
    window.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true
    })
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true })
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    }
  }, [])

  const externalTooltipHandler = useCallback(
    ({
      chart,
      tooltip
    }: {
      chart: Chart
      tooltip: TooltipModel<'line' | 'bar'>
    }) => {
      if (tooltip.opacity === 0 || scrollingRef.current) {
        setPosition(HIDDEN)
        return
      }

      const canvasRect = chart.canvas.getBoundingClientRect()
      const tooltipEl = tooltipRef.current

      const rawLeft = canvasRect.left + tooltip.caretX
      const rawTop = canvasRect.top + tooltip.caretY

      const tooltipWidth = tooltipEl?.offsetWidth ?? TOOLTIP_FALLBACK_WIDTH
      const tooltipHeight = tooltipEl?.offsetHeight ?? TOOLTIP_FALLBACK_HEIGHT
      const vpW = window.innerWidth
      const vpH = window.innerHeight

      const fitsRight =
        rawLeft + TOOLTIP_GAP_PX + tooltipWidth + VIEWPORT_PADDING_PX <= vpW
      const fitsLeft =
        rawLeft - TOOLTIP_GAP_PX - tooltipWidth >= VIEWPORT_PADDING_PX
      let left: number
      if (fitsRight) {
        left = rawLeft + TOOLTIP_GAP_PX
      } else if (fitsLeft) {
        left = rawLeft - TOOLTIP_GAP_PX - tooltipWidth
      } else {
        left = Math.max(
          VIEWPORT_PADDING_PX,
          vpW - tooltipWidth - VIEWPORT_PADDING_PX
        )
      }

      const centeredTop = rawTop - tooltipHeight / 2
      const top = Math.max(
        VIEWPORT_PADDING_PX,
        Math.min(centeredTop, vpH - tooltipHeight - VIEWPORT_PADDING_PX)
      )

      setPosition({ left, top })

      const dataPoints = tooltip.dataPoints ?? []
      const title = tooltip.title?.[0] ?? ''

      const bandHighIdx = dataPoints.findIndex(
        p => p.dataset.label === BAND_HIGH_LABEL
      )
      const bandLowIdx = dataPoints.findIndex(
        p => p.dataset.label === BAND_LOW_LABEL
      )

      let forecastRange: TooltipData['forecastRange'] = null
      if (bandHighIdx !== -1 && bandLowIdx !== -1) {
        const high = dataPoints[bandHighIdx].parsed.y
        const low = dataPoints[bandLowIdx].parsed.y
        if (high != null && low != null) {
          forecastRange = { high, low }
        }
      }

      const dataIndex = dataPoints[0]?.dataIndex ?? -1

      const items: TooltipItem[] =
        getItemsRef?.current && dataIndex >= 0
          ? getItemsRef.current(dataIndex)
          : dataPoints
              .filter(
                p =>
                  ![BAND_HIGH_LABEL, BAND_LOW_LABEL].includes(
                    p.dataset.label ?? ''
                  )
              )
              .map(p => {
                const ds = p.dataset as {
                  borderColor?: string
                  borderDash?: number[]
                }
                return {
                  datasetLabel: p.dataset.label ?? '',
                  value: p.parsed.y,
                  color: ds.borderColor ?? '#000',
                  dashed:
                    Array.isArray(ds.borderDash) && ds.borderDash.length > 0,
                  isBand: false
                }
              })

      setTooltipData({ title, items, forecastRange })
    },
    []
  )

  return { tooltipData, position, tooltipRef, externalTooltipHandler }
}
